// Plan 24c, Task 6 — Minigame Results screen (Screen 23)
//
// Fixed overlay showing ranked placements after a minigame completes.
// Each row uses createScoreboardRow + a reward pill (keys / item).
// 10s auto-continue GSAP timer + manual CONTINUE button.

import type { ScreenHandle, ScreenMountContext } from './types';
import type { BoardMountPayload } from './board-payload';
import type { GsapTimelineHandle } from '@client/renderer/vfx/gsap-registry';
import { createScoreboardRow } from '../components/scoreboard-row';
import type { ScoreboardRowHandle } from '../components/scoreboard-row';
import { createButton } from '../components/button';
import type { UIComponentHandle } from '../components/types';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type MinigamePlacement = {
  playerId: string;
  rank: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  keysReward: number; // render '+N' if positive, '-N' if negative, '--' if zero
  itemReward?: { id: string; name: string };
};

export type MinigameResultsPayload = BoardMountPayload & {
  placements: MinigamePlacement[];
  readyCount: number;
  totalCount: number;
  autoContinueSeconds?: number; // default 10
  onContinue?: () => void; // default: router.navigate('round-summary', payload)
};

// ─────────────────────────────────────────────────────────────────────────────
// Token constants — exact values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

const Z_INDEX_OVERLAY = 350;
const OVERLAY_BG = 'rgba(0, 0, 0, 0.60)';
const PANEL_BG = '#1E2332';
const RADIUS_LG = '12px';
const SHADOW_PANEL = '0 4px 12px rgba(0,0,0,0.50)';
const PANEL_PADDING = '24px 28px';
const PANEL_WIDTH = '520px';
const HEADLINE_FAMILY = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif";
const TITLE_SHADOW = '0 3px 0 #000000, 0 6px 12px rgba(0,0,0,0.50)';
const H1_SIZE = '48px';
const CAPTION_SIZE = '14px';
const COLOR_AMBER = '#E8A020';
const COLOR_KEY_GOLD = '#FFD700';
const COLOR_TEXT_DANGER = '#E04040';
const COLOR_TEXT_MUTED = '#707888';
const COLOR_TEXT_SECONDARY = '#B0B8C8';
const COLOR_PURPLE_MID = '#9A60C8';
const COLOR_FIRST_GOLD = '#FFD700';
const SHADOW_GLOW_AMBER = '0 0 12px rgba(232,160,32,0.50)';
const STAGGER_DELAY = 0.04;
const PLAYER_COLORS = [
  '#E04040', // 0 red
  '#4080D0', // 1 blue
  '#40B040', // 2 green
  '#E8D020', // 3 yellow
  '#E08820', // 4 orange
  '#9040C0', // 5 purple
  '#E06090', // 6 pink
  '#40B0C0', // 7 cyan
  '#E8E8E8', // 8 white
  '#404040', // 9 black
];

/** Max HP spec constant */
const MAX_HP = 30;

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createMinigameResultsScreen(
  ctx: ScreenMountContext<MinigameResultsPayload>,
): ScreenHandle {
  const { host, gsapRegistry, router, payload } = ctx;
  const { state, placements, readyCount, totalCount, autoContinueSeconds, onContinue } = payload;

  let disposed = false;

  const rowHandles: ScoreboardRowHandle[] = [];
  const staggerHandles: GsapTimelineHandle[] = [];
  let btnHandle: UIComponentHandle | null = null;
  let autoHandle: GsapTimelineHandle | null = null;

  // ── Helper: navigate or call onContinue ─────────────────────────────────────

  function doContinue(): void {
    if (disposed) return;
    if (onContinue) {
      onContinue();
    } else {
      router.navigate('round-summary', payload);
    }
  }

  // ── Root overlay (fixed full-screen backdrop) ────────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-minigame-results';
  root.setAttribute('data-screen-id', 'minigame-results');
  root.style.cssText = [
    'position: fixed',
    'inset: 0',
    `z-index: ${Z_INDEX_OVERLAY}`,
    'pointer-events: auto',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    `background: ${OVERLAY_BG}`,
  ].join('; ');

  // ── Centered panel ──────────────────────────────────────────────────────────

  const panel = document.createElement('div');
  panel.className = 'minigame-results-panel';
  panel.style.cssText = [
    `width: ${PANEL_WIDTH}`,
    'max-width: calc(100vw - 32px)',
    `background: ${PANEL_BG}`,
    `border-radius: ${RADIUS_LG}`,
    `box-shadow: ${SHADOW_PANEL}`,
    `padding: ${PANEL_PADDING}`,
    'display: flex',
    'flex-direction: column',
    'gap: 8px',
  ].join('; ');

  // ── Title "Results!" ────────────────────────────────────────────────────────

  const title = document.createElement('h1');
  title.className = 'minigame-results-title';
  title.textContent = 'Results!';
  title.style.cssText = [
    `font-family: ${HEADLINE_FAMILY}`,
    `font-size: ${H1_SIZE}`,
    `color: ${COLOR_AMBER}`,
    `text-shadow: ${TITLE_SHADOW}`,
    'margin: 0 0 8px',
    'text-align: center',
    'font-weight: 700',
    'line-height: 1',
  ].join('; ');

  panel.appendChild(title);

  // ── Placement rows ──────────────────────────────────────────────────────────

  const rowsContainer = document.createElement('div');
  rowsContainer.className = 'minigame-results-rows';
  rowsContainer.style.cssText = ['display: flex', 'flex-direction: column', 'gap: 6px'].join('; ');

  for (let i = 0; i < placements.length; i++) {
    const placement = placements[i];
    const player = state.players.get(placement.playerId);
    const playerColor = PLAYER_COLORS[(player?.color ?? 0) % 10] ?? PLAYER_COLORS[0];

    // Row wrapper
    const rowWrapper = document.createElement('div');
    rowWrapper.className = 'minigame-results-row-wrapper';
    rowWrapper.style.cssText = ['display: flex', 'flex-direction: column', 'gap: 4px'].join('; ');

    // First place highlight
    if (placement.rank === 1) {
      rowWrapper.style.borderLeft = `4px solid ${COLOR_FIRST_GOLD}`;
      rowWrapper.style.boxShadow = SHADOW_GLOW_AMBER;
    }

    // Scoreboard row
    const rowMount = document.createElement('div');
    rowMount.className = 'minigame-results-row-mount';

    const rowHandle = createScoreboardRow({
      gsapRegistry,
      mount: rowMount,
      playerName: player?.name ?? placement.playerId,
      playerColor,
      currentHP: player?.hp ?? MAX_HP,
      maxHP: MAX_HP,
      keys: player?.keys ?? 0,
      trophies: player?.trophies ?? 0,
      rank: placement.rank,
    });

    rowHandles.push(rowHandle);
    rowWrapper.appendChild(rowMount);

    // Reward pill
    const rewardPill = document.createElement('div');
    rewardPill.className = 'minigame-results-reward-pill';
    rewardPill.style.cssText = [
      'display: flex',
      'align-items: center',
      'justify-content: flex-end',
      'gap: 6px',
      `font-size: ${CAPTION_SIZE}`,
      'font-weight: 600',
      'padding: 0 4px',
    ].join('; ');

    // Keys reward text
    const keysSpan = document.createElement('span');
    if (placement.keysReward > 0) {
      keysSpan.textContent = `+${placement.keysReward} Keys`;
      keysSpan.style.color = COLOR_KEY_GOLD;
    } else if (placement.keysReward < 0) {
      keysSpan.textContent = `-${Math.abs(placement.keysReward)} Keys`;
      keysSpan.style.color = COLOR_TEXT_DANGER;
    } else {
      keysSpan.textContent = '--';
      keysSpan.style.color = COLOR_TEXT_MUTED;
    }
    rewardPill.appendChild(keysSpan);

    // Item reward text (if present)
    if (placement.itemReward) {
      const itemSpan = document.createElement('span');
      itemSpan.textContent = ` +1 ${placement.itemReward.name}`;
      itemSpan.style.color = COLOR_PURPLE_MID;
      rewardPill.appendChild(itemSpan);
    }

    rowWrapper.appendChild(rewardPill);
    rowsContainer.appendChild(rowWrapper);

    // Stagger entrance animation
    const tl = gsapRegistry.create(`minigame-results:row-${i}`);
    tl.timeline.fromTo(
      rowWrapper,
      { x: -20, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.3 },
      i * STAGGER_DELAY,
    );
    staggerHandles.push(tl);
  }

  panel.appendChild(rowsContainer);

  // ── CONTINUE button ─────────────────────────────────────────────────────────

  const btnMount = document.createElement('div');
  btnMount.className = 'minigame-results-btn-mount';
  btnMount.style.cssText = ['margin-top: 8px'].join('; ');

  btnHandle = createButton({
    gsapRegistry,
    mount: btnMount,
    variant: 'primary',
    label: 'CONTINUE',
    onClick: doContinue,
  });

  panel.appendChild(btnHandle.element);

  // ── Progress indicator ──────────────────────────────────────────────────────

  const progressIndicator = document.createElement('div');
  progressIndicator.className = 'minigame-results-progress';
  progressIndicator.textContent = `${readyCount}/${totalCount} ready`;
  progressIndicator.style.cssText = [
    `font-size: ${CAPTION_SIZE}`,
    `color: ${COLOR_TEXT_SECONDARY}`,
    'text-align: center',
  ].join('; ');

  panel.appendChild(progressIndicator);

  root.appendChild(panel);
  host.appendChild(root);

  // ── Auto-continue timer ─────────────────────────────────────────────────────

  autoHandle = gsapRegistry.create('minigame-results:auto');
  autoHandle.timeline.call(
    () => {
      doContinue();
    },
    undefined,
    autoContinueSeconds ?? 10,
  );

  // ── dispose ─────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Kill auto-continue timer
    if (autoHandle) {
      autoHandle.dispose();
      autoHandle = null;
    }

    // Kill all stagger timeline handles
    for (const handle of staggerHandles) {
      handle.dispose();
    }
    staggerHandles.length = 0;

    // Dispose all row handles
    for (const rowHandle of rowHandles) {
      rowHandle.dispose();
    }
    rowHandles.length = 0;

    // Dispose button handle
    if (btnHandle) {
      btnHandle.dispose();
      btnHandle = null;
    }

    // Remove root from DOM
    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return { element: root, dispose };
}
